import type L1ReviewRepository from "@/core/compliance/repositories/L1ReviewRepository";
import {
  L1ReviewStatus,
  L1TriggerReason,
  type L1ReviewItem,
} from "@/core/compliance/entities/L1Review";
import {
  RepositoryResult,
  RepositoryResultFactory,
} from "@/core/shared/types/RepositoryResult";
import type { PaginatedResult } from "@/core/shared/types/PaginatedResult";

const MOCK_REVIEWS: L1ReviewItem[] = [
  {
    reviewId: "rev-001",
    scoringEventId: "evt-001",
    businessKey: "CUST-00012345",
    status: L1ReviewStatus.PENDING,
    triggerReason: L1TriggerReason.PEP_HIT,
    trackAScore: 42.5,
    trackBScore: 78.3,
    slaDeadline: new Date(Date.now() + 12 * 60 * 60 * 1000).toISOString(),
    slaExpired: false,
    firstName: null,
    lastName: null,
    riskLevel: null,
    assignedTo: null,
    assignedAt: null,
    createdAt: new Date(Date.now() - 36 * 60 * 60 * 1000).toISOString(),
  },
  {
    reviewId: "rev-002",
    scoringEventId: "evt-002",
    businessKey: "CUST-00098765",
    status: L1ReviewStatus.PENDING,
    triggerReason: L1TriggerReason.SANCTIONS_HIT,
    trackAScore: 31.0,
    trackBScore: 92.1,
    slaDeadline: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
    slaExpired: true,
    firstName: null,
    lastName: null,
    riskLevel: null,
    assignedTo: null,
    assignedAt: null,
    createdAt: new Date(Date.now() - 50 * 60 * 60 * 1000).toISOString(),
  },
  {
    reviewId: "rev-003",
    scoringEventId: "evt-003",
    businessKey: "CUST-00054321",
    status: L1ReviewStatus.PENDING,
    triggerReason: L1TriggerReason.ADVERSE_MEDIA,
    trackAScore: 58.7,
    trackBScore: 65.4,
    slaDeadline: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
    slaExpired: false,
    firstName: null,
    lastName: null,
    riskLevel: null,
    assignedTo: null,
    assignedAt: null,
    createdAt: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
  },
];

export default class FakeL1ReviewRepositoryBackend implements L1ReviewRepository {
  private reviews: L1ReviewItem[] = [...MOCK_REVIEWS];

  private paginate(
    all: L1ReviewItem[],
    page: number,
    size: number,
  ): RepositoryResult<PaginatedResult<L1ReviewItem>> {
    const items = all.slice(page * size, page * size + size);
    return RepositoryResultFactory.success({ items, totalElements: all.length });
  }

  async getPendingReviews(
    page: number,
    size: number,
  ): Promise<RepositoryResult<PaginatedResult<L1ReviewItem>>> {
    await new Promise((resolve) => setTimeout(resolve, 600));
    const pending = this.reviews.filter((r) => r.status === L1ReviewStatus.PENDING);
    return this.paginate(pending, page, size);
  }

  async getReviewsByStatus(
    status: L1ReviewStatus,
    page: number,
    size: number,
  ): Promise<RepositoryResult<PaginatedResult<L1ReviewItem>>> {
    await new Promise((resolve) => setTimeout(resolve, 600));
    const filtered = this.reviews.filter((r) => r.status === status);
    return this.paginate(filtered, page, size);
  }

  async authorizeReview(reviewId: string): Promise<RepositoryResult<void>> {
    await new Promise((resolve) => setTimeout(resolve, 400));
    this.reviews = this.reviews.map((r) =>
      r.reviewId === reviewId ? { ...r, status: L1ReviewStatus.AUTHORIZED } : r,
    );
    return RepositoryResultFactory.success(undefined);
  }

  async suspendReview(reviewId: string, _reason: string): Promise<RepositoryResult<void>> {
    await new Promise((resolve) => setTimeout(resolve, 400));
    this.reviews = this.reviews.map((r) =>
      r.reviewId === reviewId ? { ...r, status: L1ReviewStatus.SUSPENDED } : r,
    );
    return RepositoryResultFactory.success(undefined);
  }

  async getReviewById(reviewId: string): Promise<RepositoryResult<L1ReviewItem>> {
    await new Promise((resolve) => setTimeout(resolve, 300));
    const found = this.reviews.find((r) => r.reviewId === reviewId);
    return RepositoryResultFactory.success(found ?? MOCK_REVIEWS[0]);
  }

  async rejectReview(reviewId: string, _motif: string): Promise<RepositoryResult<void>> {
    await new Promise((resolve) => setTimeout(resolve, 400));
    this.reviews = this.reviews.map((r) =>
      r.reviewId === reviewId ? { ...r, status: L1ReviewStatus.REJECTED } : r,
    );
    return RepositoryResultFactory.success(undefined);
  }

  async assignReview(reviewId: string): Promise<RepositoryResult<void>> {
    await new Promise((resolve) => setTimeout(resolve, 300));
    this.reviews = this.reviews.map((r) =>
      r.reviewId === reviewId
        ? { ...r, assignedTo: "me", assignedAt: new Date().toISOString() }
        : r,
    );
    return RepositoryResultFactory.success(undefined);
  }

  async escalateReview(reviewId: string): Promise<RepositoryResult<void>> {
    await new Promise((resolve) => setTimeout(resolve, 400));
    this.reviews = this.reviews.map((r) =>
      r.reviewId === reviewId ? { ...r, status: L1ReviewStatus.ESCALATED } : r,
    );
    return RepositoryResultFactory.success(undefined);
  }
}
